import { RefObject } from "react";

export function useSheet(tabbedSheetHandles?: string[]) {
  /** Handles navigation for both internal tabs and external sheets based on the clicked tab handle
   * - For internal tabs: Updates the active tab
   * - For external tabs: Opens the external sheet with the corresponding tab
   */
  const handleTabNavigation = (
    containerRef: RefObject<HTMLDivElement>,
    setActiveTab: (tabHandle: string) => void,
    onChange?: (tabHandle: string) => void,
  ) => {
    if (!containerRef.current) return;

    // Get all elements that have a data-tab-to-navigate attribute
    const elements = containerRef.current.querySelectorAll(
      "[data-tab-to-navigate]",
    );

    if (!elements || elements.length === 0) return;

    const handleClick = (e: Event) => {
      e.preventDefault();
      const currentElement = e.currentTarget as HTMLElement;
      const tabHandle = currentElement?.getAttribute("data-tab-to-navigate");

      if (!tabHandle) return;

      if (onChange) {
        onChange(tabHandle);
        setActiveTab(tabHandle);
      }
    };

    // Add a click event listener to all elements that have a data-tab-to-navigate attribute
    elements.forEach((link) => {
      link.addEventListener("click", handleClick);
    });

    // Return cleanup function
    return () => {
      elements.forEach((link) => {
        link.removeEventListener("click", handleClick);
      });
    };
  };

  /** Adds a click event listener to all links in the sheet container
   * to determine if the sheet should be closed or not
   * when clicked.
   */
  const sheetLinkClicks = (
    containerRef: RefObject<HTMLDivElement>,
    handleSheetToggle: () => void,
  ) => {
    if (!containerRef.current) return;

    const links = containerRef.current.querySelectorAll(
      "a:not([data-tab-to-navigate])",
    );

    if (!links) return;

    links.forEach((link) => {
      if ((link as HTMLAnchorElement).target === "_blank") return;

      link.addEventListener("click", handleSheetToggle);
    });

    return () => {
      links.forEach((link) => {
        link.removeEventListener("click", handleSheetToggle);
      });
    };
  };

  return {
    handleTabNavigation,
    sheetLinkClicks,
  };
}
